// React hook for feature access control
import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { doc } from 'firebase/firestore';
import useClaimsReadySnapshot from './useClaimsReadySnapshot';
import {
  hasFeatureAccess,
  getAvailableFeatures,
  isSubscriptionExpired,
  isSubscriptionExpiringSoon,
  getDaysUntilExpiry,
  getStorageInfo,
  FEATURES
} from '../utils/featureGating';
import { subscriberTier, tierName as tierDisplayName, tierAllows as tierAllowsFeature } from '../constants/plans';
import { isDownloadsSuspended, nextResetAt } from '../utils/downloadAllowance';

// What the gates need from the subscriber document, remembered per workspace
// in this browser. Until the live document arrives the hook has nothing, and
// nothing reads as Basic with every feature off: a Pro workspace then showed
// the Basic menu and "Basic plan" texts for as long as the first snapshot took
// (over seven seconds on a slow start, 2026-10-07). The remembered copy only
// decides what is drawn in that gap; the API and the rules decide access.
const CACHE_PREFIX = 'featureAccess:';

export const readCachedAccess = (subscriberId) => {
  if (!subscriberId) return null;
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_PREFIX + subscriberId));
    return cached && cached.id === subscriberId ? cached : null;
  } catch (e) {
    return null;
  }
};

const rememberAccess = (data) => {
  try {
    const expires = data.expires_at;
    const trialEnds = data.trial_expires_at;
    localStorage.setItem(CACHE_PREFIX + data.id, JSON.stringify({
      id: data.id,
      tier: data.tier,
      plan: data.plan,
      features: data.features,
      active: data.active,
      // A Timestamp does not survive JSON; the gates accept milliseconds.
      expires_at: expires?.toDate ? expires.toDate().getTime() : expires,
      // The term of a trial with no `expires_at` (#977); the gates read both.
      trial_expires_at: trialEnds?.toDate ? trialEnds.toDate().getTime() : trialEnds,
    }));
  } catch (e) {
    // Private window or full storage: the page works without the copy.
  }
};

const forgetAccess = (subscriberId) => {
  try {
    localStorage.removeItem(CACHE_PREFIX + subscriberId);
  } catch (e) {
    // Nothing to forget.
  }
};

export const useFeatureAccess = () => {
  const auth = useAuth() || {};
  const { currentUser } = auth;
  // The chosen workspace. Older test doubles of the auth context do not
  // report one, and then the stored choice is used, as this hook always did.
  const subscriberId = auth.subscriberId !== undefined
    ? auth.subscriberId
    : localStorage.getItem('subscriberId');
  const [subscriberData, setSubscriberData] = useState(() => readCachedAccess(subscriberId));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Another workspace's remembered copy must not decide this one's menu.
  const shownForRef = useRef(subscriberId);
  useEffect(() => {
    if (shownForRef.current === subscriberId) return;
    shownForRef.current = subscriberId;
    setSubscriberData(readCachedAccess(subscriberId));
    setError(null);
  }, [subscriberId]);

  // Real-time listener for the subscriber document. Mounted in the app bar,
  // which is on screen before a workspace is chosen, so it waits for the ID
  // token to carry the workspace: a listener refused for want of the claim is
  // dead, and this one used to stay dead for the whole session, leaving the
  // menu on the remembered (or Basic) copy. See useClaimsReadySnapshot.
  const { status } = useClaimsReadySnapshot(
    (sid) => (currentUser ? doc(db, 'subscribers', sid) : null),
    {
      subscriberId: subscriberId || null,
      label: 'Subscriber data',
      onNext: (snap) => {
        if (snap.exists()) {
          const data = { id: snap.id, ...snap.data() };
          setSubscriberData(data);
          rememberAccess(data);
          setError(null);
        } else {
          setError('Subscriber not found');
          setSubscriberData(null);
          forgetAccess(subscriberId);
        }
        setLoading(false);
      },
      onError: (err) => {
        console.error('Error fetching subscriber data:', err);
        setError('Failed to load subscription data');
        setLoading(false);
      },
    },
    [Boolean(currentUser)]
  );

  // Nothing to wait for: no workspace, nobody signed in, or a token that can
  // never read the document (a customer-portal session).
  useEffect(() => {
    if (status === 'idle') setLoading(false);
  }, [status]);

  // Helper functions that use the current subscriber data
  const checkFeature = (feature) => hasFeatureAccess(subscriberData, feature);

  // The tier the workspace is on. A document without one reads as Basic (the
  // legacy default) — see constants/plans.js.
  const tier = subscriberTier(subscriberData);
  const tierAllows = (feature) => tierAllowsFeature(tier, feature);

  // Every flag, keyed by feature id, for consumers that filter a list (the
  // settings menu). One object so the app bar and the settings rail cannot
  // pass different sets; memoised so a useMemo keyed on it is not defeated.
  const featureFlags = useMemo(
    () => Object.fromEntries(
      Object.values(FEATURES).map((feature) => [feature, hasFeatureAccess(subscriberData, feature)])
    ),
    [subscriberData]
  );

  return {
    // Raw data
    subscriberData,
    loading,
    error,

    // Tier
    tier,
    tierName: tierDisplayName(tier),
    isPro: tier === 'pro',
    tierAllows,
    featureFlags,

    // Feature checks
    hasTaskManagement: checkFeature(FEATURES.TASK_MANAGEMENT),
    hasAssetTracking: checkFeature(FEATURES.ASSET_TRACKING),
    hasIssueTracking: checkFeature(FEATURES.ISSUE_TRACKING),
    hasAdvancedAnalytics: checkFeature(FEATURES.ADVANCED_ANALYTICS),
    hasApiAccess: checkFeature(FEATURES.API_ACCESS),
    hasMcpAccess: checkFeature(FEATURES.MCP_ACCESS),
    hasFullTextSearch: checkFeature(FEATURES.FULL_TEXT_SEARCH),
    hasAssignments: checkFeature(FEATURES.ASSIGNMENTS),
    hasIntegrations: checkFeature(FEATURES.INTEGRATIONS),
    hasMassExport: checkFeature(FEATURES.MASS_EXPORT),
    hasSso: checkFeature(FEATURES.SSO),
    hasCustomerPortal: checkFeature(FEATURES.CUSTOMER_PORTAL),
    hasCustomTemplates: checkFeature(FEATURES.CUSTOM_TEMPLATES),

    // Subscription status
    isExpired: isSubscriptionExpired(subscriberData),
    isExpiringSoon: isSubscriptionExpiringSoon(subscriberData),
    daysUntilExpiry: getDaysUntilExpiry(subscriberData),

    // Monthly download allowance (#764). Suspended only while the flag
    // belongs to the current Asia/Singapore month; the reset is the next 1st,
    // 00:00 SGT. The API refuses the downloads either way — this is what lets
    // the page say so before the click.
    downloadsSuspended: isDownloadsSuspended(subscriberData),
    downloadsResetAt: nextResetAt(),

    // Utility functions
    checkFeature,
    availableFeatures: getAvailableFeatures(subscriberData),
    storageInfo: getStorageInfo(subscriberData)
  };
};
